// PersonAufFirmaDialog.tsx — Paket A7 (Plan 2026-10-01, User 02.10.2026):
// In der Personenakte wird eine Person aus „Haushalt" oder „Beziehungen
// außerhalb des Haushaltes" auf eine BEREITS verbundene Firma (Zeile in
// „Verbindung zu Unternehmen") gezogen. Dann öffnet dieses Popup
// „Verantwortung fürs Unternehmen" für genau diese Person an genau dieser Firma
// (beide vorbelegt) und speichert SOFORT — nicht erst beim Schloss.
//
// Sofort-Speichern wie die Karte „Verantwortung fürs Unternehmen" der
// Firmenakte (akteSofortSpeichern.ts): nur mit Schreibrecht, nie über
// ungespeicherte Formularänderungen hinweg, danach lädt die Akte neu.
// Gespeichert wird additiv über attach-menschen (vorhandene Rollen der Person
// an der Firma bleiben stehen).

import * as React from "react";
import { EintragDialog } from "../forms/EintragDialog";
import {
  VerantwortlichkeitInput,
  type VerantwortlichkeitValue,
} from "../verantwortlichkeit/VerantwortlichkeitInput";
import { buildMenschenItems } from "../verantwortlichkeit/buildMenschenItems";
import { attachMenschen } from "../../services/menschenFuersUnternehmenService";
import { refName } from "./beziehungsAnzeige";
import { UNGESPEICHERT_HINWEIS, type AkteSofortSpeichern } from "./akteSofortSpeichern";
import { useRelStyles } from "./styles";
import type { ContactRef } from "./types";

/** Kann diese Ablage sofort gespeichert werden? Sonst der Grund. */
export function personAufFirmaPruefen(
  person: ContactRef | null | undefined,
  firma: ContactRef | null | undefined,
  sofort: Pick<AkteSofortSpeichern, "darfSchreiben" | "hatUngespeicherte"> | null,
): string | null {
  if (!sofort) return "Das geht nur in einer gespeicherten Akte.";
  if (!sofort.darfSchreiben) return "Für diese Akte fehlt das Schreibrecht.";
  if (!firma?.contactId) return "Die Firma ist noch nicht gespeichert — bitte zuerst speichern (Schloss schließen).";
  if (!person?.contactId) return "Die Person ist noch nicht gespeichert — bitte zuerst speichern (Schloss schließen).";
  if (sofort.hatUngespeicherte()) return UNGESPEICHERT_HINWEIS;
  return null;
}

export function PersonAufFirmaDialog({
  person,
  firma,
  sofort,
  onSchliessen,
  speichern = async (items) => {
    await attachMenschen(items, false);
  },
}: {
  person: ContactRef;
  firma: ContactRef;
  sofort: AkteSofortSpeichern;
  onSchliessen: () => void;
  /** Nur für Tests: Ersatz für attach-menschen. */
  speichern?: (items: ReturnType<typeof buildMenschenItems>) => Promise<void>;
}) {
  const rs = useRelStyles();
  const [verant, setVerant] = React.useState<VerantwortlichkeitValue>({
    isExtern: false,
    roles: [],
    externalCompany: null,
  });
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [laeuft, setLaeuft] = React.useState(false);
  const leer = !verant.roles.length && !verant.istMenschImUnternehmen;

  const uebernehmen = async () => {
    if (leer || laeuft) return;
    // Zwischen Ablegen und Speichern kann im Formular etwas geändert worden sein.
    if (sofort.hatUngespeicherte()) return setFehler(UNGESPEICHERT_HINWEIS);
    const items = buildMenschenItems([{ ref: person, verant }], firma.contactId!);
    if (!items.length) return;
    setLaeuft(true);
    try {
      await speichern(items);
      onSchliessen();
      sofort.neuLaden();
    } catch {
      setFehler("Die Verantwortung konnte nicht gespeichert werden. Bitte erneut versuchen.");
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <EintragDialog
      titel="Verantwortung fürs Unternehmen"
      kicker="Hinzufügen"
      fehler={fehler}
      uebernehmenText="Speichern"
      uebernehmenGesperrt={leer}
      beschaeftigt={laeuft}
      onUebernehmen={() => void uebernehmen()}
      onAbbrechen={onSchliessen}
    >
      <p className={rs.hint}>
        <strong>{refName(person) || "Person"}</strong> bei <strong>{refName(firma) || "Firma"}</strong> — wird sofort
        gespeichert.
      </p>
      <VerantwortlichkeitInput
        idBase={`person-auf-firma-${person.contactId}-${firma.contactId}`}
        hideExternalCompany
        value={verant}
        onChange={setVerant}
        legalFormId={firma.legalFormId}
        neuerEintrag
      />
    </EintragDialog>
  );
}
